<template>
  <div class="card pmr-card">
    <PageHeader
        :showBtn="false"
        title="Pending Money Receipt"
    >
      <template #extra-btn>
        <router-link :to="{ name: 'sales-invoice-online-pay', params: route.params, query: route.query }" class="btn btn-primary pmr-head-btn">
          Pay Online
        </router-link>
      </template>
    </PageHeader>
    <div class="pmr-toolbar">
      <div class="pmr-dates">
        <div class="pmr-field">
          <label class="pmr-label">From</label>
          <input v-model="startDate" @change="updateDateQuery" type="date" class="form-control pmr-date">
        </div>
        <div class="pmr-field">
          <label class="pmr-label">To</label>
          <input v-model="endDate" @change="updateDateQuery" type="date" class="form-control pmr-date">
        </div>
        <button type="button" class="btn btn-primary pmr-go" @click="onClickDateGo">Go</button>
      </div>
      <div class="pmr-actions">
        <button v-show="selectedTab === 'invoice'" type="button" class="btn pmr-btn" @click="goToPrint">
          <i class="fa-solid fa-print"></i>
          Print
        </button>
        <FilterPopover
            title="Filters"
            popover-width="560px"
            :active-count="activeFilterCount"
            @apply="onClickGo"
            @clear="onClearFilters"
        >
          <div class="col-12 col-sm-6" v-if="selectedTab === 'invoice'">
            <label class="filter-label">Sales Person</label>
            <AsyncSelect
                placeholder="Select Sales Person"
                v-model="singleSR"
                :api-service="fetchAllSR"
            />
          </div>
          <div class="col-12 col-sm-6">
            <div class="d-flex align-items-center justify-content-between">
              <label class="filter-label">Customer</label>
              <span class="pmr-search-icon" @click="onOpenContactSearchModal">
                <i class="fas fa-search"></i>
              </span>
            </div>
            <AsyncSelect
                placeholder="Select Customer"
                v-model="contactProfile"
                :api-service="fetchContactProfiles"
                :additional-query="additionalContactOption"
                :format-label="formatPatientLabel"
                :additional-option="additionalOption"
                :key="contactRenderKey"
            />
          </div>
          <div class="col-12 col-sm-6" v-if="selectedTab === 'invoice'">
            <label class="filter-label">Client</label>
            <AsyncSelect
                placeholder="Client Name, ID, Mobile No"
                v-model="selectedClient"
                :api-service="fetchContactProfileDropdownList"
                :additional-query="{ roles: 'client' }"
                :format-label="formatContactProfileLabel"
                label="name"
                :reduce="client => client"
            />
          </div>
          <div class="col-12 col-sm-6" v-if="selectedTab === 'invoice'">
            <label class="filter-label">Order Type</label>
            <v-select
                placeholder="Select Order Type"
                v-model="saleType"
                :options="orderTypes"
                label="name"
                :reduce="name => name.value"
            />
          </div>
          <div class="col-12 col-sm-6" v-if="selectedTab === 'invoice'">
            <label class="filter-label">Bill Type</label>
            <v-select
                multiple
                placeholder="Select Bill Type"
                v-model="billType"
                :options="billTypeList"
                label="label"
                :reduce="label => label.value"
            />
          </div>
          <div class="col-12 col-sm-6" v-if="selectedTab === 'invoice'">
            <label class="filter-label">Business Unit</label>
            <v-select
                placeholder="Select Business Unit"
                v-model="businessUnit"
                :options="business"
                label="name"
                :reduce="name => name.id"
            />
          </div>
          <div class="col-12 col-sm-6" v-if="selectedTab === 'invoice'">
            <label class="filter-label">Payment Status</label>
            <v-select
                placeholder="Select Payment Status"
                v-model="selectedPaymentStatus"
                :options="paymentStatus"
                label="name"
                :reduce="option => option.value"
            />
          </div>
        </FilterPopover>
      </div>
    </div>
    <div class="pmr-tabs" role="tablist">
      <button type="button" role="tab" class="pmr-tab" :class="{ 'is-active': selectedTab === 'invoice' }" @click="onClickTab('invoice')">Invoice</button>
      <button type="button" role="tab" class="pmr-tab" :class="{ 'is-active': selectedTab === 'receipt' }" @click="onClickTab('receipt')">Receipt</button>
    </div>
    <div class="pmr-body">
      <ListTable
          v-if="selectedTab === 'invoice'"
          :tableItems="tableItems"
          :currentPage="invoiceData && invoiceData.data && invoiceData.data.current_page"
          :lastPage="invoiceData && invoiceData.data && invoiceData.data.last_page"
          :totalValue="invoiceData && invoiceData.total"
      />
      <MoneyReceiptListTable
          v-if="selectedTab === 'receipt'"
          :tableItems="moneyReceiptList"
          :isHideEditAndReturnButton="true"
          @print="fetchAndPrintReceipt"
      />
    </div>
    <div class="pmr-pagination">
      <Pagination :offset="params.offset" @onPageChange="onPageChange" ref="pagination"/>
    </div>
    <ContactDropdownFilter
        v-if="store.state.isModalOpenThree"
        @onSearchContact="onSearchContact"
        :companyRoles="companyRoles"
        :isFilterable="true"
    />
    <Loader v-if="loading"/>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Pagination from '@/components/atom/Pagination'
import ListTable from '@/components/molecule/company/inventory/pending-money-receipt/ListTable.vue'
import MoneyReceiptListTable from '@/components/molecule/company/inventory/money-receipt/ListTable.vue'
import PageHeader from '@/components/atom/PageHeader.vue';
import FilterPopover from '@/components/atom/FilterPopover.vue';
import handleCrm from '@/services/modules/crm/crm.js'
import handleContact from "@/services/modules/contact";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import {useAsyncDropdownHelper} from "@/services/utils/asyncDropdownHelper";
import handlePurchase from '@/services/modules/purchase'
import handleCBusinesses from '@/services/modules/businesses'
import handleInventory from "@/services/modules/inventory";
import Loader from '@/components/atom/LoaderComponent'
import ContactDropdownFilter from '@/components/atom/Contact/ContactDropdownFilter.vue'
import handleRole from "@/services/modules/role";
import pdfPrinter from '@/services/utils/pdf/pendingMoneyReceipt';
import handleCompany from "@/services/modules/company";
import handlePurchaseAndSales from "@/services/modules/purchase";
import moneyReceiptHelper from "@/services/utils/pdf/moneyReceiptHelper";
import handleMoneyReceipt from "@/services/moneyReceipt.js";
import handleDropdowns from "@/services/modules/dropdowns";

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false)
const companyRoles = ref(null);
const tableItems = ref([]);
let business = ref([])
const { fetchMoneyReceipt } = handlePurchaseAndSales();
const { generatePdf } = moneyReceiptHelper();
const { fetchMoneyReceiptList } = handleMoneyReceipt();
const params = reactive({
  searchTxt: '',
  offset: 10
});
const billTypeList = ref([])
const billType = ref(null)

const contactProfile = ref(null)
const selectedClient = ref(null)
const singleSR = ref(null);
const singleDealer = ref(null);
const area = ref(null);
const dealerRenderKey = ref(0);
const contactRenderKey = ref(0);
const saleType = ref(null);
const selectedInvoiceStatus = ref(null);
const businessUnit = ref(null);
const invoiceStatuses = ref([]);
const invoiceData = ref( {});
const selectedPaymentStatus = ref(null);
const selectedTab = ref('invoice');
const moneyReceiptList = ref([]);
const paymentStatus = ref([]);
const pdfMoneyReceipt = ref([]);
const pdfTotal = ref({});
const companyInfo = ref({})

const additionalOption = ref([]);
const additionalDealerQuery = ref({});
const additionalContactOption = ref({
  with_mobile: 1,
  dealer_id: ''
});
const orderTypes = ref([]);
const payload = ref({
  'billType': null,
  'orderType': null,
  'customerName': null,
  'clientName': null,
  'businessUnit': null,
  'paymentStatus': null
})

const {fetchHome} = handleInventory()
const { fetchAllDealers, fetchAllSR, fetchAreaList } = handleCrm();
const { fetchCompanyDefaultRoles } = handleRole();
const { fetchContactProfiles } = handleContact();
const { formatPatientLabel, formatContactProfileLabel } = useAsyncDropdownHelper();
const { fetchContactProfileDropdownList } = handleDropdowns();
const { fetchSalesRegisterList, fetchSalesOrderTypes, fetchOrderStatus } = handlePurchase();
const {fetchBusinessList} = handleCBusinesses();
const { exportToPDF } = pdfPrinter();
const { fetchCompanyInfo, fetchActiveCustomizationField } = handleCompany();
const start = computed(() => route.query.start);
const end = computed(() => route.query.end);
const startDate = ref(route.query.start || '');
const endDate = ref(route.query.end || '');
const activeFilterCount = computed(() => {
  if (selectedTab.value !== 'invoice') return contactProfile.value ? 1 : 0;
  const filled = [singleSR.value, contactProfile.value, selectedClient.value, saleType.value, businessUnit.value, selectedPaymentStatus.value].filter(value => !! value).length;
  return filled + (billType.value && billType.value.length ? 1 : 0);
});

const orderNumber = ref('');
const invoiceNumber = ref('');

watch(() => singleSR.value, (newValue, oldValue) => {
  additionalDealerQuery.value = {};
  additionalContactOption.value.dealer_id = '';
  singleDealer.value = null;
  contactProfile.value = null;
  dealerRenderKey.value += 1;
  contactRenderKey.value += 1;

  if(newValue) {
    additionalDealerQuery.value = {'sale_representative_id': newValue.id}
  }
})

watch(() => singleDealer.value, (newValue) => {
  additionalContactOption.value.dealer_id = '';
  contactProfile.value = null;
  contactRenderKey.value += 1;

  if(newValue) {
    additionalContactOption.value.dealer_id = newValue.id
  }
})

const goToPrint = async() => {

  const date = { 'startDate': start.value, 'endDate': end.value };

  loading.value = true;

  await getSaleRegisterList(false);
  await Promise.all([
    fetchCompanyInfo(route.params.companyId),
    fetchActiveCustomizationField(`?company_id=${route.params.companyId}&customization_type=pdf&type=pending_money_receipt_pdf`).catch(() => ({}))
  ]).then(([companyRes, customizationRes]) => {
    companyInfo.value = companyRes.data
    loading.value = false;
    exportToPDF(companyInfo.value, pdfMoneyReceipt.value.data, pdfTotal.value, date, payload.value, customizationRes.status ? customizationRes.data : {});
  }).catch(err => {
    console.log(err)
  }).finally(() => {
    loading.value = false
  })
}

const getCompanyRoles = async () => {
  const res = await fetchCompanyDefaultRoles(`?company_id=${route.params.companyId}&roles=customer,patient`);
  if (res.status) {
    companyRoles.value = res.data
  }
}

const onClickToExport = () => {
  let token = localStorage.getItem('token');
  let url = `${process.env.VUE_APP_BASE_URL}/export/inventory/sales-register${getQuery()}&_token=${token}&is_export=1`;
  let a = document.createElement("a")
  a.setAttribute("href", url)
  a.click()
}

const onOpenContactSearchModal = () => {
  store.state.isModalOpenThree = true;
}

const onSearchContact = (contact) => {
  contactProfile.value = contact
}

const buildDateQuery = () => {
  const routeQuery = Object.assign({}, route.query, { start: startDate.value, end: endDate.value });
  if (! startDate.value) delete routeQuery.start;
  if (! endDate.value) delete routeQuery.end;
  return routeQuery;
}

const updateDateQuery = async () => {
  await router.push({ path: route.path, query: buildDateQuery() });
}

const onClickDateGo = async () => {
  await updateDateQuery();
  onClickGo();
}

const onClearFilters = () => {
  singleSR.value = null;
  contactProfile.value = null;
  selectedClient.value = null;
  saleType.value = null;
  billType.value = null;
  businessUnit.value = null;
  selectedPaymentStatus.value = 'due';
  onClickGo();
}

const onClickGo = () => {
  store.commit("resetPagination")
  if (selectedTab.value === 'invoice') {
    return getSaleRegisterList()
  }
  getMoneyReceiptList()
}

const setPaginationData = (data) => {
  store.commit('setPaginationData', data)
};

const setPagination = (data) => {
  setPaginationData({
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
}

const getOrderStatus = async () => {
  const res = await fetchOrderStatus(`?company_id=${route.params.companyId}`);

  if (res.status) {
    invoiceStatuses.value = res.data.filter(item => ['created', 'pending'].includes(item.value))
  }
}

const getBusinessList = async () => {
  const res = await fetchBusinessList(`?company_id=${route.params.companyId}`);

  if (res.status) {
    business.value = res.data
  }
}

const onPageChange = async (page) => {
  let routeQuery = Object.assign({}, route.query)
  routeQuery.page = page
  await router.push({path: route.path, query: routeQuery})
  
  if (selectedTab.value === 'invoice') {
    return getSaleRegisterList()
  }
  
  getMoneyReceiptList()
}

const getSaleRegisterList = async (pagination = true) => {

  const query = getQuery(pagination);
  loading.value = true

  await fetchSalesRegisterList(query).then(res => {
    loading.value = false
    if (!res.status){
      tableItems.value = []
      invoiceData.value = {}
      store.commit("resetPagination")
      return
    }
    if(! pagination ) {
      pdfMoneyReceipt.value = res.data;
      pdfTotal.value = res.total;
      return;
    }

    tableItems.value = res.data.data;
    invoiceData.value = res
    setPagination(res.data)
  }).catch(err => {
    console.log(err)
  }).finally(() => {
    loading.value = false
  })
}

const getMoneyReceiptList = async (pagination = true) => {
  let mrQuery = `?company_id=${route.params.companyId}`;
  if (start.value && end.value) mrQuery += `&start_date=${start.value}&end_date=${end.value}`;
  if (route.query.page) mrQuery += '&page=' + route.query.page;
  if (contactProfile.value) mrQuery += `&contact_profile_id=${contactProfile.value.id}`;
  mrQuery += '&offset=' + params.offset;
  if (!pagination) mrQuery += '&is_all=' + !!pagination;

  loading.value = true;
  await fetchMoneyReceiptList(mrQuery).then(res => {
    if (!res.status) {
      moneyReceiptList.value = [];
      invoiceData.value = {};
      store.commit("resetPagination");
      return;
    }
    moneyReceiptList.value = res.data.data;
    invoiceData.value = res;
    setPagination(res.data);
  }).catch(err => {
    console.log(err);
  }).finally(() => {
    loading.value = false;
  });
}

const getSaleOrderTypes = async () => {
  const res = await fetchSalesOrderTypes(`?company_id=${route.params.companyId}`);
  if (res.status) {
    orderTypes.value = res.data
  }
}

const getQuery = (pagination) => {
  let query = `?company_id=${route.params.companyId}`;
  if (start.value && end.value) query += `&start_date=${start.value}&end_date=${end.value}`
  if (route.query.page) query += '&page=' + route.query.page
  if(singleSR.value) query += `&sale_representative_id=${singleSR.value.id}`;
  if(singleDealer.value) query += `&dealer_id=${singleDealer.value.id}`;
  if(contactProfile.value) {
    query += `&contact_profile_id=${contactProfile.value.id}`;
    payload.value.customerName = contactProfile.value.name;
  }
  if(selectedClient.value) query += `&contact_profile_client_id=${selectedClient.value.id}`;
  payload.value.clientName = selectedClient.value ? formatContactProfileLabel(selectedClient.value) : null;
  if(area.value) query += `&area_id=${area.value.id}`;
  if(orderNumber.value) query += `&order_number=${orderNumber.value}`;
  if(invoiceNumber.value) query += `&invoice_number=${invoiceNumber.value}`;
  if(saleType.value) {
    query += `&sale_type=${saleType.value}`;
    payload.value.orderType = orderTypes.value.find(el => el.value == saleType.value).name
  } 

  if(selectedInvoiceStatus.value) query += '&invoice_status='+selectedInvoiceStatus.value

  if(selectedPaymentStatus.value) query += '&payment_status='+selectedPaymentStatus.value;
  payload.value.paymentStatus = selectedPaymentStatus.value ? selectedPaymentStatus.value.toUpperCase() : null;

  if(businessUnit.value) {
    query += '&business_unit='+businessUnit.value;
    payload.value.businessUnit = business.value.find(el => el.id === businessUnit.value).name;
  }

  if (!!billType.value && billType.value.length > 0) {
    query += '&bill_type=' + billType.value
    payload.value.billType = billTypeList.value.find(el => el.value == billType.value).label;
  }

  query += '&offset=' + params.offset
  if(! pagination) query += '&is_all='+!! pagination
  return query;
}

function navigateToListPage() {
  const query = Object.assign({}, route.query)
  delete query.visit_client_id;
  delete query.type;

  router.push({
    name:route.query.type === 'approval'
        ? 'sales-order-approval'
        : (route.query.type === 'order-sales'
            ? 'order-sales'
            : 'sales-order-list'),
    params: route.params, query: query
  })
}
const getPaymentStatus = () => {
  let paymentStatusValue = [
    {name: 'Due', value: 'due'},
    {name: 'Paid', value: 'paid'}
  ]
  paymentStatus.value = paymentStatusValue
  
  selectedPaymentStatus.value = paymentStatusValue[0].value;
  if (route.query.payment_status === 'success' || route.query.payment_status === 'paid') {
    selectedPaymentStatus.value = 'paid';
  }
}

const onClickTab = (tab) => {
  selectedTab.value = tab;
  store.commit("resetPagination")
  if (tab === 'invoice') {
    return getSaleRegisterList();
  }
  getMoneyReceiptList();
}



const fetchAndPrintReceipt = async (receiptId) => {
  const companyQuery = `?company_id=${route.params.companyId}`;
  
  try {
    const companyRes = await fetchCompanyInfo(route.params.companyId);
    if (companyRes.status) {
      companyInfo.value = companyRes.data;
    }
    
    const receiptRes = await fetchMoneyReceipt(receiptId, companyQuery);
    if (receiptRes && receiptRes.status) {
      generatePdf(companyInfo.value, receiptRes.data);
    }
  } catch (err) {
    console.log(err);
  }
};

onMounted(() => {
  getSaleOrderTypes()
  getOrderStatus()
  getBusinessList()
  getPaymentStatus()
  getCompanyRoles()
  fetchHome().then(res => {
    if (res.data) {
      for (const value of Object.keys(res.data.hospital_invoice_types)) {
        billTypeList.value.push({
          value: value,
          label: res.data.hospital_invoice_types[value]
        })
      }
    }
  })

  if (route.query.tab) {
    selectedTab.value = route.query.tab;
  }
  
  if (selectedTab.value === 'invoice') {
    return getSaleRegisterList();
  }
  getMoneyReceiptList();
})
</script>

<style scoped>
.pmr-card {
  margin-bottom: 0;
  overflow: visible;
}
.pmr-head-btn {
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}
.pmr-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 20px 12px;
}
.pmr-dates {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
}
.pmr-field {
  display: flex;
  flex-direction: column;
}
.pmr-label {
  margin-bottom: 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: #101828;
}
.pmr-date {
  width: 170px;
  height: 38px;
  border-color: #e4e7ec;
  border-radius: 8px;
}
.pmr-go {
  min-width: 72px;
  height: 38px;
  border-radius: 8px;
}
.pmr-actions {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-left: auto;
}
.pmr-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 14px;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  background: #fff;
  color: #475467;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}
.pmr-btn:hover {
  background: #f9fafb;
  color: #101828;
}
.pmr-search-icon {
  margin-bottom: 4px;
  padding: 0 2px;
  font-size: 12px;
  color: #98a2b3;
  cursor: pointer;
}
.pmr-search-icon:hover {
  color: #7367f0;
}
.pmr-tabs {
  display: flex;
  gap: 4px;
  margin: 0 20px;
  border-bottom: 1px solid #eaecf0;
  overflow-x: auto;
  overflow-y: hidden;
}
.pmr-tab {
  margin-bottom: -1px;
  padding: 10px 14px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: #667085;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: color .15s ease, border-color .15s ease;
}
.pmr-tab:hover {
  color: #101828;
}
.pmr-tab.is-active {
  border-bottom-color: #7367f0;
  color: #7367f0;
  font-weight: 600;
}
.pmr-body {
  padding: 16px 20px 0;
}
.pmr-pagination {
  padding: 12px 20px 16px;
}
@media (max-width: 576px) {
  .pmr-toolbar {
    padding: 14px 16px 10px;
  }
  .pmr-dates {
    width: 100%;
  }
  .pmr-field {
    flex: 1 1 130px;
  }
  .pmr-date {
    width: 100%;
  }
  .pmr-actions {
    width: 100%;
    justify-content: flex-end;
  }
  .pmr-tabs {
    margin: 0 16px;
  }
  .pmr-body {
    padding: 12px 16px 0;
  }
  .pmr-pagination {
    padding: 12px 16px 16px;
  }
}
</style>
